import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

/** Matches `--ease-wi-overshoot` / `--ease-wi-reveal`. */
export const REVEAL_EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1.4];
/** Stagger between bars (ms). */
export const REVEAL_STAGGER_MS = 150;
/** @deprecated Prefer {@link REVEAL_STAGGER_MS}; seconds form for legacy math. */
export const REVEAL_STAGGER_S = REVEAL_STAGGER_MS / 1000;
/** Width transition duration (ms). */
export const REVEAL_BAR_DURATION_MS = 800;
/** @deprecated Prefer {@link REVEAL_BAR_DURATION_MS}. */
export const REVEAL_BAR_DURATION_S = REVEAL_BAR_DURATION_MS / 1000;

export type RevealBarProps = {
	/** Option label shown above the track. */
	label: string;
	/** Normalized score 0–100. */
	value: number;
	/** Winner uses `wi-gold`; others use `wi-neutral-bar`. */
	isWinner?: boolean;
	/** Stagger delay before this bar reveals (ms). */
	delayMs?: number;
	/**
	 * When true, animate width from 0 after `delayMs`.
	 * When false (already-finalized cold load), render final width immediately.
	 */
	animate?: boolean;
	className?: string;
};

/**
 * The Reveal — labeled results bar with internal track + trigger (site-transformation PRD §2.5).
 * Callers pass `label` / `value` / `isWinner` / `delayMs` only — no thin-fill adapter.
 */
export function RevealBar({
	label,
	value,
	isWinner = false,
	delayMs = 0,
	animate = false,
	className = "",
}: RevealBarProps) {
	const reduceMotion = useReducedMotion() === true;
	const play = animate && !reduceMotion;
	const [revealed, setRevealed] = useState(!play);
	const pct = Math.min(100, Math.max(0, value));
	const fillPct = pct > 0 ? Math.max(pct, 2) : 0;

	useEffect(() => {
		if (!play) {
			setRevealed(true);
			return;
		}
		setRevealed(false);
		const t = window.setTimeout(() => setRevealed(true), delayMs);
		return () => window.clearTimeout(t);
	}, [play, delayMs, value, label]);

	return (
		<div className={["mb-2", className].filter(Boolean).join(" ")}>
			<div className="mb-[3px] font-sans text-caption text-wi-text">{label}</div>
			<div
				className="h-2 overflow-hidden rounded-[5px] bg-wi-border-strong"
				aria-hidden="true"
			>
				<div
					className={[
						"h-full rounded-[5px]",
						isWinner ? "bg-wi-gold" : "bg-wi-neutral-bar",
					].join(" ")}
					style={{
						width: revealed ? `${fillPct}%` : "0%",
						transition: play
							? "width 800ms var(--ease-wi-overshoot), background-color 400ms ease"
							: undefined,
					}}
				/>
			</div>
			<span className="sr-only">
				{label}: {Math.round(pct)}
				{isWinner ? " (winner)" : ""}
			</span>
		</div>
	);
}
